import forge from 'apis/forge';
import Icon, { IconType } from 'components/Icon';
import FrameConversation from 'components/Panel/Chat/Conversation/FrameConversation';
import { useEffect } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { selectActiveFrameChat, selectFrame } from 'redux/db/reducers';
import { setActiveFrameChatId } from 'redux/mode/actions';
import { RootReducer } from 'redux/reducers';
import { useTheme } from 'styled-components';
import { frameSiteUrl } from 'utils';
import * as Panel from '../../ChatPanel/styles';
import * as S from './styles';

interface FrameChatPanelProps {
	frameId: string;
}

const FrameChatPanel = ({ frameId }: FrameChatPanelProps) => {
	const dispatch = useDispatch();
	const theme = useTheme();

	const { frame, frameChat } = useSelector(
		(r: RootReducer) => ({
			frame: selectFrame(r.db, frameId),
			frameChat: selectActiveFrameChat(r.db),
		}),
		shallowEqual
	);

	const siteUrl = frame ? frameSiteUrl(frame) : null;
	const isImageFrame = !siteUrl;

	// Fetch existing frameChatId on mount, clear on unmount
	useEffect(() => {
		let cancelled = false;
		const fetchChatId = async () => {
			try {
				const resp = await forge.getFrameChatId({ frameId });
				if (!cancelled && resp?.frameChatId) {
					dispatch(setActiveFrameChatId(resp.frameChatId));
				}
			} catch {
				// no frame chat yet
			}
		};
		fetchChatId();
		return () => {
			cancelled = true;
			dispatch(setActiveFrameChatId(null));
		};
	}, [frameId, dispatch]);

	//---------------------------------------------------------------------------------------------------
	// RENDERING
	//---------------------------------------------------------------------------------------------------
	if (!frame) return null;

	const renderEmptyState = () => {
		return (
			<S.EmptyState>
				<S.EmptyStateIcon>
					<Icon type={IconType.FRAME} width={48} height={48} color={theme.gray[400]} />
				</S.EmptyStateIcon>
				<S.EmptyStateText>No editing available</S.EmptyStateText>
				<S.EmptyStateSubtext>
					This panel is for editing prototype frames. Imported images don't support version history
					or editing.
				</S.EmptyStateSubtext>
			</S.EmptyState>
		);
	};

	const renderContent = () => {
		if (isImageFrame) {
			return renderEmptyState();
		}

		if (!frameChat) {
			// Frame chat hasn't loaded yet — show conversation with frame entries only.
			// Pass a synthetic empty chat so the conversation can still render version cards.
			return (
				<FrameConversation
					frame={frame}
					frameChat={{
						id: '',
						frameId: frame.id,
						entries: [],
						metadata: {} as any,
						createdAt: new Date(),
						updatedAt: new Date(),
						writeId: '',
					}}
				/>
			);
		}

		return <FrameConversation frame={frame} frameChat={frameChat} />;
	};

	return <Panel.Base>{renderContent()}</Panel.Base>;
};

export default FrameChatPanel;
